import React from 'react';
import ActiveFiltersBar from './ActiveFiltersBar';

/**
 * TrackingToolbar - Standardized toolbar for tracking dashboards
 * 
 * Provides consistent layout and styling for:
 * - Search input
 * - Record count badge
 * - Active filters display
 * - Action buttons (Import, Export, Template, Column Config, Add New)
 * - Optional batch delete
 * 
 * @param {Object} props
 */
const TrackingToolbar = ({
  // Search
  searchTerm = '',
  onSearchChange,
  searchPlaceholder = 'Search records...',
  
  // Record count
  filteredCount,
  totalCount,
  
  // Columns count (optional)
  displayedColumnsCount,
  totalColumnsCount,
  
  // Filters display
  activeFilters = [],
  hasActiveFilters = false,
  onClearFilter,
  onClearAll,
  
  // Action handlers (button order: Import, Export, Template, Column Config, Analytics, Add New)
  onImport,
  onExport,
  onDownloadTemplate,
  onColumnConfig,
  onAnalytics,
  onAddNew,
  onRefresh,
  
  // Batch delete
  selectedCount = 0,
  onBatchDelete,
  
  // Accent color for hover effects
  accentColor = '#dc2626',
  
  // Custom badge content
  customBadges = null,
}) => {
  // Button style with hover effect
  const getButtonStyle = (isActive = false) => ({
    padding: '0.5rem 1rem',
    backgroundColor: isActive ? accentColor : '#f3f4f6',
    color: isActive ? 'white' : '#374151',
    border: `1px solid ${isActive ? accentColor : '#e5e7eb'}`,
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
  });

  const handleButtonHover = (e, isEnter) => {
    if (isEnter) {
      e.target.style.backgroundColor = accentColor;
      e.target.style.color = 'white';
      e.target.style.borderColor = accentColor;
    } else {
      e.target.style.backgroundColor = '#f3f4f6';
      e.target.style.color = '#374151';
      e.target.style.borderColor = '#e5e7eb';
    }
  };

  return (
    <div style={{
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '0.75rem',
    }}>
      {/* Left side: Search, counts, filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        {/* Search input */}
        {onSearchChange && (
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{
                padding: '6px 12px',
                paddingLeft: '30px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '14px',
                width: '250px',
              }}
            />
            <span style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d',
            }}>
              🔍
            </span>
          </div>
        )}
        
        {/* Record count */}
        <span style={{
          backgroundColor: '#6c757d',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '0.85rem',
        }}>
          {filteredCount !== undefined ? filteredCount : totalCount} records
        </span>
        
        {/* Columns count */}
        {displayedColumnsCount !== undefined && displayedColumnsCount < totalColumnsCount && (
          <span style={{
            backgroundColor: '#17a2b8',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.85rem',
          }}>
            {displayedColumnsCount}/{totalColumnsCount} columns
          </span>
        )}
        
        {/* Custom badges */}
        {customBadges}
        
        {/* Active filters */}
        {hasActiveFilters && (
          <ActiveFiltersBar
            activeFilters={activeFilters}
            searchTerm={searchTerm}
            onClearFilter={onClearFilter}
            onClearSearch={() => onSearchChange('')}
            onClearAll={onClearAll}
          />
        )}
      </div>
      
      {/* Right side: Action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {/* Batch delete - if items selected */}
        {selectedCount > 0 && onBatchDelete && (
          <button
            onClick={onBatchDelete}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            🗑️ Delete ({selectedCount})
          </button>
        )}
        
        {/* Import button */}
        {onImport && (
          <button
            onClick={onImport}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            📤 Import
          </button>
        )}
        
        {/* Export button */}
        {onExport && (
          <button
            onClick={onExport}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            📥 Export
          </button>
        )}
        
        {/* Template button */}
        {onDownloadTemplate && (
          <button
            onClick={onDownloadTemplate}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            📋 Template
          </button>
        )}
        
        {/* Column Config button */}
        {onColumnConfig && (
          <button
            onClick={onColumnConfig}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            ⚙️ Column Config
          </button>
        )}
        
        {/* Analytics button */}
        {onAnalytics && (
          <button
            onClick={onAnalytics}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            📊 Analytics
          </button>
        )}
        
        {/* Add New button */}
        {onAddNew && (
          <button
            onClick={onAddNew}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            ➕ Add New
          </button>
        )}
        
        {/* Refresh button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            style={getButtonStyle()}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            🔄 Refresh
          </button>
        )}
      </div>
    </div>
  );
};

export default TrackingToolbar;
